import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, CreditCard } from 'lucide-react';
import { cardBillingAPI } from '../services/api';

const BillingResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [polling, setPolling] = useState(true);
  const pollRef = useRef(null);
  const orderId = searchParams.get('order') || '';
  const outcome = searchParams.get('outcome') || '';

  useEffect(() => {
    if (!orderId) return;
    const poll = async () => {
      try {
        const res = await cardBillingAPI.getPaymentStatus(orderId);
        setPayment(res.data);
        if (['paid', 'failed', 'cancelled', 'needs_review'].includes(res.data.status)) {
          setPolling(false);
        }
      } catch { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  useEffect(() => {
    if (!polling && pollRef.current) clearInterval(pollRef.current);
  }, [polling]);

  const status = payment?.status || (outcome === 'success' ? 'processing' : outcome === 'declined' ? 'failed' : 'processing');

  const icons = {
    paid: <CheckCircle2 className="h-16 w-16 text-green-500" />,
    failed: <XCircle className="h-16 w-16 text-red-500" />,
    cancelled: <XCircle className="h-16 w-16 text-gray-400" />,
    processing: <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />,
    pending: <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />,
    needs_review: <AlertTriangle className="h-16 w-16 text-amber-500" />,
  };

  const titles = {
    paid: 'Payment Successful!',
    failed: 'Payment Declined',
    cancelled: 'Payment Cancelled',
    processing: 'Processing Payment...',
    pending: 'Processing Payment...',
    needs_review: 'Payment Under Review',
  };

  const messages = {
    paid: `Your subscription has been activated. Receipt: ${payment?.receipt_number || ''}`,
    failed: 'Your card was declined. Please try again with a different card.',
    cancelled: 'Payment was cancelled. No charges were made.',
    processing: 'Please wait while we confirm your payment. This usually takes a few seconds.',
    pending: 'Please wait while we confirm your payment.',
    needs_review: 'Your payment is being reviewed by our team. You will receive an email once resolved.',
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="billing-result-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">{icons[status] || icons.processing}</div>
          <CardTitle className="text-xl">{titles[status] || 'Processing...'}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">{messages[status] || 'Processing your payment...'}</p>

          {payment?.card_last_four && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <CreditCard className="h-4 w-4" />
              {payment.card_type} •••• {payment.card_last_four}
            </div>
          )}

          {payment?.amount && status === 'paid' && (
            <p className="text-2xl font-bold text-green-600">${payment.amount.toFixed(2)} USD</p>
          )}

          <div className="flex flex-col gap-2 pt-4">
            {status === 'paid' && (
              <Button onClick={() => navigate('/dashboard')} data-testid="go-dashboard-btn">
                Go to Dashboard
              </Button>
            )}
            {status === 'failed' && (
              <Button onClick={() => navigate('/subscription')} data-testid="try-again-btn">
                Try Again
              </Button>
            )}
            {['cancelled', 'needs_review'].includes(status) && (
              <Button variant="outline" onClick={() => navigate('/subscription')}>
                Back to Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingResultPage;
